import { Lightning, Utils } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { CmsBorder } from './CmsBorder';
import { CmsConstants } from '../../Business/cms/CmsConstants';
import { CmsCardImage } from './CmsCardImage';
import { CmsProgress } from './CmsProgress';
import { CardInfo } from '../../Business/cms/CardInfo';
import { CmsActionResolver } from '../../Business/cms/cardResolver/CmsActionResolver';
import { ActionMethod, AssetType } from '../../api/models/Shared.types';
import { FavoriteRequestType } from '../../Business/cms/FavoriteRibbonRequest';

export class CmsCard extends Lightning.Component {
  /**
   * @type {CardDataSource}
   */
  data;
  static _template() {
    const dims = CmsConstants.Card;
    const padding = dims.imageOffset + dims.imageOffset;
    return {
      x: 0,
      y: 0,
      w: w => w,
      h: h => h,
      Content: {
        x: dims.imageOffset,
        y: dims.imageOffset,
        w: w => w - padding,
        h: h => h - padding,
        Background: {
          w: w => w,
          h: 0,
          rect: false,
          color: Theme.Colors.Ribbon.Card.InfoBackground,
          shader: {
            type: Lightning.shaders.RoundedRectangle,
            radius: [0, 0, dims.innerRadius, dims.innerRadius],
          },
        },
        Image: {
          type: CmsCardImage,
          w: w => w,
          h: h => h,
        },
        Progress: {
          type: CmsProgress,
          x: 0,
          w: w => w,
          h: h => h,
        },
        ScheduleLine: {
          mount: 1,
          x: w => w,
          w: CmsConstants.Card.ScheduleLine.width,
          h: CmsConstants.Card.ScheduleLine.height,
          rect: true,
          visible: false,
          color: Theme.Colors.Ribbon.Card.ScheduleLine.Background,
          ScheduleText: {
            w: w => w,
            text: {
              ...Theme.Fonts.Ribbon.ScheduleLine,
            },
          },
        },
        TopLine: {
          x: CmsConstants.Progress.horizontalPadding,
          w: w => w - CmsConstants.Progress.horizontalPadding - CmsConstants.Progress.horizontalPadding,
          h: CmsConstants.Card.TopLine,
          text: {
            ...Theme.Fonts.Ribbon.TopLine,
          },
          color: Theme.Colors.Ribbon.Card.Text.Normal,
        },
        BottomLine: {
          x: CmsConstants.Progress.horizontalPadding,
          w: w => w - CmsConstants.Progress.horizontalPadding - CmsConstants.Progress.horizontalPadding,
          h: CmsConstants.Card.BottomLine,
          text: {
            ...Theme.Fonts.Ribbon.BottomLine,
          },
          color: Theme.Colors.Ribbon.Card.Text.Normal,
        },
        FavoriteIcon: {
          x: w => w - CmsConstants.Card.iconPadding,
          mount: 1,
          visible: false,
          w: 30,
          h: 30,
          src: Utils.asset('images/guide/ic_star.png'),
          color: Theme.Colors.Primary,
        },
        Icons: {
          x: w => w - CmsConstants.Card.iconPadding,
          h: 24,
          mount: 1,
          flex: {
            direction: 'row',
          },
        },
      },
      Border: {
        type: CmsBorder,
        alpha: 0,
      },
    };
  }

  _firstActive() {
    this.reset();
    if (!this.data) return;
    this.showFavorite = false;
    this.favoriteToggle = false;
    if (!this.info) this.info = new CardInfo(this.data);
    const szi = Object.create(this.data.sizeInfo);
    const cardImage = this.tag('Content').tag('Image');
    // console.log('Card data', this, this.info);
    if (this.loadNext) {
      this.signal('startLazyLoad');
    }
    if (
      this.data.asset.asset.subType === FavoriteRequestType.CHANNEL_LOGO ||
      this.data.asset.asset.subType === FavoriteRequestType.CHANNEL_TOGGLE
    ) {
      // Check if contain image should be used, refactor to always use contain
      // contain used on nebula cache ribbon channel cards
      cardImage.containImage = true;
    }

    this.info
      .cardInfo()
      .then(data => {
        // console.log('DATA in card', data);
        this.data.extractor = data.extractor;
        szi.checkProgressBookmark(data.bookmarkPosition);
        if (szi.showTopLine) {
          const top_text = data.topLine;
          if (!top_text) {
            //remove top line
            szi.removeTopLine();
          }
        }
        cardImage.updateRoundedRects(
          (szi.showProgress && data.bookmarkPosition) || szi.showTopLine || szi.showBottomLine
        );
        this.patch({
          h: szi.height,
          Content: {
            Background: {
              y: szi.cardHeight - 2,
              h: szi.infoSectionHeight + 2,
              rect: szi.showTopLine || szi.showBottomLine || szi.showProgress,
            },
            Image: {
              w: szi.width - CmsConstants.Card.imageOffset - CmsConstants.Card.imageOffset,
              h: szi.cardHeight,
              imageUrl: this.info.imageUrl,
              channelLogo: this.info.channelLogo,
            },
            Progress: {
              y: szi.progressOffset,
              visible: szi.showProgress && data.bookmarkPosition != null,
              fillPercentage: data.bookmarkPosition,
            },
            ScheduleLine: {
              y: szi.progressOffset,
              visible: data.scheduleLine !== '',
              ScheduleText: {
                text: {
                  text: data.scheduleLine,
                },
              },
            },
            TopLine: {
              y: szi.topLineOffset,
              visible: szi.showTopLine,
              text: {
                text: data.topLine,
                fontFace: data.topLineBold ? 'Bold' : 'Regular',
                textColor: data.topLineColor,
              },
            },
            BottomLine: {
              y: szi.bottomLineOffset,
              visible: szi.showBottomLine,
              text: {
                text: data.bottomLine,
                fontFace: data.bottomLineBold ? 'Bold' : 'Regular',
                textColor: data.bottomLineColor,
              },
            },
            FavoriteIcon: {
              y: szi.bottomLineOffset,
            },
            Icons: {
              y: this.info.useOldEpgCards ? szi.topLineOffset + 28 : szi.height - 18,
            },
          },
        });
        this._addIcons().then(numberOfIcons => {
          if (this.info.useOldEpgCards) {
            const iconWidth = numberOfIcons * (24 + 10);
            let topLineWidth = this.tag('TopLine').w;
            topLineWidth -= iconWidth;
            this.patch({
              Content: {
                TopLine: {
                  text: {
                    wordWrapWidth: topLineWidth,
                  },
                },
              },
            });
          }
        });
        if (this.data.action.method === ActionMethod.FAVOURITE) {
          this.showFavorite = true;
          this.favoriteToggle = true;
          this.tag('FavoriteIcon').visible = this.favoriteToggle;
        }
      })
      .then(() => {
        // console.log('Cms card data:', this.data, this.info);
        let d = this.data.asset.asset;
        if (this.data.extractor) {
          d = {
            ...this.data.extractor.detail,
            schedule: this.data.extractor.schedule,
          };
        }
        this.info
          .getEpgIcons()
          .then(icons => {
            d.recordingIcons = icons;
          })
          .then(() => {
            this.info
              .getFavoriteIcon()
              .then(result => {
                d.favoriteIcon = result;
              })
              .then(() => {
                this._miniDetailData = d;
                if (this.hasFocus()) this._tryToOpenMiniDetail();
              });
          });
      });
  }

  _init() {
    this._miniDetailData = undefined;
    this._loadNext = false;
    // console.log(this.data);
  }

  _focus() {
    this.tag('Border').setSmooth('alpha', 1);
    this._tryToOpenMiniDetail();
  }

  _tryToOpenMiniDetail() {
    if (
      this.data &&
      !this.data.pageProperties.includes('HIDE_MINI_DETAIL') &&
      this._miniDetailData &&
      this.data.asset.asset.subType !== FavoriteRequestType.CHANNEL_LOGO &&
      this.data.asset.asset.subType !== FavoriteRequestType.CHANNEL_TOGGLE
    ) {
      this.fireAncestors('$updateMiniDetail', this._miniDetailData);
      this.fireAncestors('$showMiniDetail');
    } else {
      this.fireAncestors('$hideMiniDetail');
    }
  }

  _unfocus() {
    this.tag('Border').setSmooth('alpha', 0);
  }

  _handleEnter() {
    const action = this.data.action;
    if (action.extra) {
      action.extra = {
        ...action.extra,
        adult: this.data.asset.asset.adult,
      };
    } else {
      action.extra = { adult: this.data.asset.asset.adult };
    }
    if (this.showFavorite) {
      this.favoriteToggle = !this.favoriteToggle;
      this.tag('FavoriteIcon').visible = this.favoriteToggle;
    }
    // console.log('CmsCard action', action, this.data);
    // if action is Detail and the asset is a Recording: change the action to Recording type so detailPage uses correct dataSource
    if (action.method === ActionMethod.DETAIL && this.data.asset.asset.type === AssetType.RECORDING) {
      action.type = AssetType.RECORDING;
      action.id = this.data.asset.asset.id;
    }
    CmsActionResolver.doAction(this, action, this.data.backend);
    return true;
  }

  /**
   * @private
   */
  _addIcons() {
    return this.info.getEpgIcons().then(icons => {
      icons.forEach((icon, idx) => {
        this.tag('Icons').childList.add({
          flexItem: { marginRight: idx < icons.length - 1 ? 10 : 0 },
          y: 24,
          mountY: 1,
          ...icon,
        });
      });
      return icons.length;
    });
  }

  set newData(dat) {
    this.reset();
    if (!this.data) {
      //no data set yet: set data and let FirstActive handle first update
      this.data = dat;
      return;
    }
    this.data = dat;
    this._firstActive();
  }

  reset() {
    this.tag('Content').tag('Image').imageUrl = '';
    this.tag('Content').tag('Image').channelLogo = '';
    this.tag('Icons').childList.clear();
  }

  clearImage() {
    this.tag('Content').tag('Image').imageUrl = '';
    this.tag('Content').tag('Image').channelLogo = '';
  }
}
